// client/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from '../config/theme';
import { AuthProvider, useAuth } from '../stores/AuthContext';
import { ThemeProvider } from '../stores/ThemeContext';
import Navbar from '../components/common/Navbar';
import Dashboard from '../features/dashboard/Dashboard';
import TradesView from '../features/trades/TradesView';
import AddTradeModal from '../features/trades/AddTradeModal';
import JournalAI from '../features/journaling/JournalAI';
import Analytics from '../features/analytics/Analytics';
import TradingDashboard from '../features/deepInsights/deepinsights';
import InsightLab from '../features/insightLab/InsightLab';
import AIAnalysis from '../features/deepInsights/AIanalysis';
import CortexAnalytics from '../features/cortex/CortexAnalytics';
import CTXPlaybook from '../features/playbook/CTXPlaybook';
import Profile from '../features/profile/ProfileSettings';
// Pages
import Login from '../features/auth/Login';
import SignUp from '../features/auth/SignUp';
import ForgotPassword from '../features/auth/ForgotPassword';
import AuthCallback from '../features/auth/AuthCallback';
import HomePage from '../features/home/HomePage';
import PricingPage from '../features/pricing/PricingPage';
import CheckoutSuccess from '../features/billing/CheckoutSuccess';
import UpgradeModal from '../components/common/UpgradeModal';
import CookieBanner from '../components/common/CookieBanner';
import Footer from '../components/common/Footer';
// Legal pages
import PrivacyPolicy from '../features/legal/PrivacyPolicy';
import TermsOfService from '../features/legal/TermsOfService';
import CookiePolicy from '../features/legal/CookiePolicy';
// Page name map for human-readable PostHog dashboard labels
const PAGE_NAMES = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/trades': 'Trades',
  '/performance': 'Performance / Edge',
  '/journal-ai': 'Chronicle',
  '/psychology': 'Cortex Psychology',
  '/ai-coach': 'Sage AI Coach',
  '/insight-lab': 'Insight Lab',
  '/playbook': 'Blueprint Playbook',
  '/ai-analysis': 'AI Analysis',
  '/pricing': 'Pricing',
  '/profile': 'Profile Settings',
  '/billing/success': 'Checkout Success',
  '/login': 'Login',
  '/signup': 'Signup',
  '/forgot-password': 'Forgot Password',
};

// Tracks SPA route changes as PostHog pageviews (must live inside <Router>)
function PostHogPageTracker() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: PAGE_NAMES[location.pathname] || location.pathname,
    });
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    console.log("not user???")
    return <Navigate to="/login" />;
  }

  return (
    <>
      <Navbar />
      {children}
    </>
  );
};

const App = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  return (
    <PostHogProvider client={posthog}>
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeProvider>
      <AuthProvider>
        <Router>
          <PostHogPageTracker />
          <UpgradeModal />
          <CookieBanner />
          <Routes>
            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trades"
              element={
                <ProtectedRoute>
                  <TradesView />
                </ProtectedRoute>
              }
            />
            {/* New primary routes */}
            <Route
              path="/performance"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbook"
              element={
                <ProtectedRoute>
                  <CTXPlaybook />
                </ProtectedRoute>
              }
            />
            <Route
              path="/journal-ai"
              element={
                <ProtectedRoute>
                  <JournalAI />
                </ProtectedRoute>
              }
            />
            <Route
              path="/psychology"
              element={
                <ProtectedRoute>
                  <CortexAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-coach"
              element={
                <ProtectedRoute>
                  <TradingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/insight-lab"
              element={
                <ProtectedRoute>
                  <InsightLab />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-analysis"
              element={
                <ProtectedRoute>
                  <AIAnalysis />
                </ProtectedRoute>
              }
            />
            {/* Redirects from old routes */}
            <Route path="/analytics" element={<Navigate to="/performance" replace />} />
            <Route path="/cortex" element={<Navigate to="/psychology" replace />} />
            <Route path="/deep-insights" element={<Navigate to="/ai-coach" replace />} />
          {/*}  <Route
              path="/homepage"
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />*/}
    <Route
              path="/profile"
              element={
                <ProtectedRoute>
                    <Profile />
                </ProtectedRoute>
              }
            />
            {/* Billing routes */}
            <Route path="/pricing" element={<><PricingPage /><Footer /></>} />
            <Route path="/billing" element={<Navigate to="/profile?tab=billing" replace />} />
            <Route
              path="/billing/success"
              element={
                <ProtectedRoute>
                  <CheckoutSuccess />
                </ProtectedRoute>
              }
            />

            {/* Legal pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />

            {/* Public routes */}
            <Route path="/login" element={<><Login /><Footer /></>} />
            <Route path="/signup" element={<><SignUp /><Footer /></>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Catch-all redirect - must be last */}
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Router>
      </AuthProvider>
      </ThemeProvider>
    </MuiThemeProvider>
    </PostHogProvider>
  );
};

export default App;